import { useState, useEffect, useRef, useCallback } from 'react'
import { CrmProcessJob } from 'api/oracleApi'
import { triggerProcess, getProcessJobStatus, getLatestProcessJob, cancelProcess } from 'api/oracleApi'

const LS_KEY = 'oracle_active_job'

export type OracleSourceType = 'telegram' | 'email' | 'calendar' | 'manual' | 'attio'

export interface UseProcessJobReturn {
    job: CrmProcessJob | null
    isRunning: boolean
    isCancelling: boolean
    startJob: (opts?: { endDate?: string; source?: OracleSourceType }) => Promise<void>
    cancelJob: () => Promise<void>
    dismissJob: () => void
}

export function useProcessJob(onComplete?: () => void): UseProcessJobReturn {
    const [activeJobId, setActiveJobId] = useState<string | null>(null)
    const [job, setJob] = useState<CrmProcessJob | null>(null)
    const [isCancelling, setIsCancelling] = useState(false)
    const onCompleteRef = useRef(onComplete)
    useEffect(() => {
        onCompleteRef.current = onComplete
    })

    // On mount: resume from localStorage or check for a running job
    useEffect(() => {
        const stored = localStorage.getItem(LS_KEY)
        if (stored) {
            setActiveJobId(stored)
            return
        }
        getLatestProcessJob()
            .then((j) => {
                if (j && j.status === 'running') {
                    localStorage.setItem(LS_KEY, j.job_id)
                    setActiveJobId(j.job_id)
                    setJob(j)
                }
            })
            .catch(() => undefined)
    }, [])

    // Polling — also enforces a 40-minute client-side timeout so stale jobs
    // never block the UI indefinitely when a server restart was missed
    useEffect(() => {
        if (!activeJobId) return
        let cancelled = false
        const CLIENT_TIMEOUT_MS = 40 * 60 * 1000

        const poll = async () => {
            try {
                const j = await getProcessJobStatus(activeJobId)
                if (cancelled) return
                // Client-side safety: treat a very old running job as failed
                if (j.status === 'running' && Date.now() - new Date(j.started_at).getTime() > CLIENT_TIMEOUT_MS) {
                    setJob({ ...j, status: 'failed', error: 'Process timed out' })
                    localStorage.removeItem(LS_KEY)
                    setActiveJobId(null)
                    onCompleteRef.current?.()
                    return
                }
                setJob(j)
                if (j.status !== 'running') {
                    localStorage.removeItem(LS_KEY)
                    setActiveJobId(null)
                    onCompleteRef.current?.()
                }
            } catch {
                // ignore transient errors
            }
        }

        void poll()
        const interval = setInterval(poll, 3000)
        return () => {
            cancelled = true
            clearInterval(interval)
        }
    }, [activeJobId])

    const startJob = useCallback(async (opts?: { endDate?: string; source?: OracleSourceType }) => {
        const { jobId } = await triggerProcess(opts)
        localStorage.setItem(LS_KEY, jobId)
        setJob(null)
        setIsCancelling(false)
        setActiveJobId(jobId)
    }, [])

    const cancelJob = useCallback(async () => {
        if (!activeJobId) return
        setIsCancelling(true)
        try {
            await cancelProcess(activeJobId)
        } catch {
            // Even on failure, the next poll will reflect actual state
        }
    }, [activeJobId])

    const dismissJob = useCallback(() => {
        localStorage.removeItem(LS_KEY)
        setActiveJobId(null)
        setJob(null)
        setIsCancelling(false)
    }, [])

    return { job, isRunning: !!activeJobId, isCancelling, startJob, cancelJob, dismissJob }
}
